import React, { useEffect, useRef, useState, useCallback } from "react";
import { post } from "../../lib/http.js";
import { API } from "../../config/api.js";

const isLikelyMobile = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /android|iphone|ipad|ipod|iemobile|mobile/i.test(ua);
};

const tryParseJSON = (txt) => {
  try {
    return JSON.parse(txt);
  } catch {
    return null;
  }
};

const parsePayload = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return { kind: "empty", raw: text };

  const asJSON = tryParseJSON(text);
  if (asJSON?.ticketId) {
    return { kind: "ticket", raw: text, ticketId: String(asJSON.ticketId) };
  }
  if (/^TKT-/i.test(text)) {
    return { kind: "ticket", raw: text, ticketId: text };
  }
  return { kind: "code", raw: text };
};

const useBeepOrVibrate = () => {
  return useCallback(() => {
    try {
      if ("vibrate" in navigator) navigator.vibrate?.(40);
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 1200;
      g.gain.value = 0.03;
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      setTimeout(() => {
        o.stop();
        ctx.close();
      }, 80);
    } catch {}
  }, []);
};

function StatusPill({ state, msg }) {
  let cls = "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border";
  if (state === "success") cls += " bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (state === "error") cls += " bg-rose-50 text-rose-700 border-rose-200";
  else if (state === "scanning") cls += " bg-blue-50 text-blue-700 border-blue-200";
  else cls += " bg-white/10 text-white border-white/20";
  return (
    <span className={cls}>
      <span
        className={`h-2 w-2 rounded-full ${
          state === "success"
            ? "bg-emerald-500"
            : state === "error"
            ? "bg-rose-500"
            : state === "scanning"
            ? "bg-blue-500 animate-pulse"
            : "bg-white/50"
        }`}
      />
      {msg}
    </span>
  );
}

const STAGE_LABELS = {
  VERIFIED: "Donor Pass Verified",
  READY: "Ready to scan",
  TODAY_LIMIT_OVER: "Aaj ki limit khatam",
  INSUFFICIENT_REMAINING: "Itni entry nahi bachi",
  TOTAL_LIMIT_OVER: "Donor Pass invalid",
  EVENT_ENDED: "Event khatam",
  EVENT_NOT_STARTED: "Event shuru nahi hua",
  NOT_FOUND: "Donor Pass nahi mili",
  INVALID_QR: "Galat QR",
  SERVER_ERROR: "Server error",
};

function normalizeScanResponse(resp) {
  const safe = resp || {};
  const success = !!safe.success;
  const stage = safe.stage || (success ? "VERIFIED" : "UNKNOWN");
  const message =
    safe.message ||
    (success ? "Donor Pass verified successfully." : STAGE_LABELS[stage] || "Scan failed");
  return {
    success,
    stage,
    message,
    ticketId: safe.ticketId || null,
    eventName: safe.eventName || null,
    category: safe.category || null,
    bookingCode: safe.bookingCode || null,
    quantity: safe.quantity != null ? Number(safe.quantity) : null,
    usedToday: safe.usedToday != null ? Number(safe.usedToday) : null,
    remainingToday: safe.remainingToday != null ? Number(safe.remainingToday) : null,
    entryCount: safe.entryCount != null ? Number(safe.entryCount) : null,
    eventDays: Array.isArray(safe.eventDays) ? safe.eventDays : null,
  };
}

export default function QRScanMobile() {
  const [status, setStatus] = useState({ state: "idle", msg: "Initializing…" });
  const [busy, setBusy] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const html5QrcodeRef = useRef(null);
  const html5ModuleRef = useRef(null);
  const lastScanAtRef = useRef(0);
  const beep = useBeepOrVibrate();

  const startScanner = useCallback(async () => {
    try {
      const { Html5Qrcode } = html5ModuleRef.current;
      const elId = "qr-reader-mobile";
      if (!html5QrcodeRef.current) {
        html5QrcodeRef.current = new Html5Qrcode(elId, false);
      }
      const cameraConfig = { facingMode: { exact: "environment" } };
      setStatus({ state: "scanning", msg: "Starting camera…" });

      await html5QrcodeRef.current.start(
        cameraConfig,
        {
          fps: 15,
          qrbox: (vw, vh) => {
            const minEdge = Math.min(vw, vh);
            const box = Math.floor(minEdge * 0.6);
            return { width: box, height: box };
          },
          rememberLastUsedCamera: true,
          formatsToSupport: [html5ModuleRef.current.Html5QrcodeSupportedFormats.QR_CODE],
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        onDecoded,
        () => {}
      );

      try {
        const caps = await html5QrcodeRef.current.getRunningTrackCapabilities();
        setTorchAvailable(!!caps?.torch || !!caps?.fillLightMode);
      } catch {
        setTorchAvailable(false);
      }

      setStatus({ state: "scanning", msg: "Point camera at QR" });
    } catch (e) {
      try {
        const { Html5Qrcode } = html5ModuleRef.current || {};
        if (!Html5Qrcode) throw e;
        const devs = await Html5Qrcode.getCameras();
        if (!devs?.length) throw e;

        await html5QrcodeRef.current.start(
          devs[0].id,
          { fps: 12, qrbox: { width: 260, height: 260 }, aspectRatio: 1.777, rememberLastUsedCamera: true },
          onDecoded,
          () => {}
        );

        try {
          const caps = await html5QrcodeRef.current.getRunningTrackCapabilities();
          setTorchAvailable(!!caps?.torch || !!caps?.fillLightMode);
        } catch {}
        setStatus({ state: "scanning", msg: "Point camera at QR" });
      } catch (err) {
        console.error("Camera start failed:", err);
        setStatus({ state: "error", msg: err?.message || "Unable to start camera" });
      }
    }
  }, []);

  const stopScanner = useCallback(async () => {
    try {
      if (html5QrcodeRef.current?.isScanning) {
        await html5QrcodeRef.current.stop();
      }
    } catch {}
    setTorchOn(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isLikelyMobile()) {
        setStatus({ state: "error", msg: "This scanner is mobile-only. Open on a phone." });
        return;
      }
      try {
        const mod = await import("html5-qrcode");
        if (cancelled) return;
        html5ModuleRef.current = mod;
        await startScanner();
      } catch (e) {
        console.error("init error:", e);
        setStatus({ state: "error", msg: e?.message || "Init failed" });
      }
    })();
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [startScanner, stopScanner]);

  const toggleTorch = async () => {
    if (!torchAvailable) return;
    try {
      if (torchOn) {
        await html5QrcodeRef.current.turnOffFlash();
        setTorchOn(false);
      } else {
        await html5QrcodeRef.current.turnOnFlash();
        setTorchOn(true);
      }
    } catch (e) {
      console.error("torch error:", e);
    }
  };

  const onDecoded = async (text) => {
    const now = Date.now();
    if (now - lastScanAtRef.current < 900) return;
    lastScanAtRef.current = now;

    beep();
    setStatus({ state: "scanning", msg: "QR read. Processing…" });

    const p = parsePayload(text);
    if (p.kind === "empty") {
      setScanResult(
        normalizeScanResponse({
          success: false,
          stage: "INVALID_QR",
          message: "Empty QR payload.",
        })
      );
      setSheetOpen(true);
      await stopScanner();
      return;
    }
    await callScanAPI(p);
  };

  const callScanAPI = async (parsed) => {
    try {
      setBusy(true);
      await stopScanner();

      const url = API?.scanner?.validateTicket || "/api/ticket/scanTicket";
      const body =
        parsed.ticketId && parsed.kind === "ticket"
          ? { ticketId: parsed.ticketId }
          : { code: parsed.raw };

      const rawResp = await post(url, body);
      const normalized = normalizeScanResponse(rawResp);
      setScanResult(normalized);
      setSheetOpen(true);

      setStatus({
        state: normalized.success ? "success" : "error",
        msg: normalized.message || (normalized.success ? "Verified" : "Scan failed"),
      });
    } catch (e) {
      const normalized = normalizeScanResponse({
        success: false,
        stage: "SERVER_ERROR",
        message: e?.message || "Scan failed",
      });
      setScanResult(normalized);
      setSheetOpen(true);
      setStatus({ state: "error", msg: normalized.message });
    } finally {
      setBusy(false);
    }
  };

  const closeSheetAndResume = async () => {
    setSheetOpen(false);
    setScanResult(null);
    setStatus({ state: "scanning", msg: "Resuming camera…" });
    setTimeout(() => startScanner(), 120);
  };

  const ok = !!scanResult?.success;
  const stage = scanResult?.stage || "";
  const message = scanResult?.message || (ok ? "Donor Pass verified successfully." : "Unable to scan");
  const title = STAGE_LABELS[stage] || (ok ? "Donor Pass Verified" : "Scan Failed");
  const ticketId = scanResult?.ticketId || null;
  const eventName = scanResult?.eventName || null;
  const category = scanResult?.category || null;
  const bookingCode = scanResult?.bookingCode || null;
  const quantity = scanResult?.quantity != null ? scanResult.quantity : null;
  const usedToday = scanResult?.usedToday != null ? scanResult.usedToday : null;
  const remainingToday = scanResult?.remainingToday != null ? scanResult.remainingToday : null;
  const entryCount = scanResult?.entryCount != null ? scanResult.entryCount : null;
  const hasDetails =
    ticketId || eventName || category || bookingCode || quantity != null || usedToday != null;

  return (
    <main
      className="w-full min-h-[100dvh] bg-black text-white"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom,0px),12px)" }}
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="text-base font-semibold">QR Scanner (Mobile)</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTorch}
            disabled={!torchAvailable || busy}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
              torchOn ? "bg-yellow-300 text-black border-yellow-400" : "bg-white/10 border-white/20"
            } disabled:opacity-40`}
            title={torchAvailable ? "Toggle torch" : "Torch not supported"}
          >
            {torchOn ? "Torch On" : "Torch"}
          </button>
        </div>
      </div>

      <div className="px-3">
        <div className="rounded-xl overflow-hidden bg-black border border-white/10">
          <div id="qr-reader-mobile" className="w-full aspect-[9/16] max-h-[70dvh]" />
        </div>
        <div className="mt-2 text-xs">
          <StatusPill state={status.state} msg={status.msg} />
        </div>
      </div>

      <div className="h-6" />

      {sheetOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Scan result">
          <button type="button" onClick={closeSheetAndResume} className="absolute inset-0 bg-black/60" aria-label="Close" />

          <div
            className="absolute inset-x-0 bottom-0 bg-white text-slate-900 rounded-t-2xl shadow-2xl p-4"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)" }}
          >
            <div className="mx-auto h-1 w-10 rounded-full bg-slate-300 mb-3" />

            <div className="flex items-center justify-between">
              <div className="text-base font-bold">{title}</div>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${
                  ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
                }`}
              >
                {stage || (ok ? "VERIFIED" : "FAILED")}
              </span>
            </div>

            <div className={`mt-2 text-sm ${ok ? "text-emerald-700" : "text-rose-700"}`}>{message}</div>

            {hasDetails && (
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {eventName && (
                  <div className="col-span-2">
                    <div className="text-slate-500">Event</div>
                    <div className="font-medium">{eventName}</div>
                  </div>
                )}
                {bookingCode && (
                  <div>
                    <div className="text-slate-500">Booking Code</div>
                    <div className="font-mono text-[13px]">{bookingCode}</div>
                  </div>
                )}
                {category && (
                  <div>
                    <div className="text-slate-500">Category</div>
                    <div className="font-medium capitalize">{category}</div>
                  </div>
                )}
                {quantity != null && (
                  <div>
                    <div className="text-slate-500">Max / day</div>
                    <div className="font-medium">{quantity}</div>
                  </div>
                )}
                {usedToday != null && (
                  <div>
                    <div className="text-slate-500">Used today</div>
                    <div className="font-medium">{usedToday}</div>
                  </div>
                )}
                {remainingToday != null && (
                  <div>
                    <div className="text-slate-500">Left today</div>
                    <div className="font-medium text-emerald-700">{remainingToday}</div>
                  </div>
                )}
                {entryCount != null && ok && (
                  <div>
                    <div className="text-slate-500">This scan</div>
                    <div className="font-medium">{entryCount}</div>
                  </div>
                )}
                {ticketId && (
                  <div className={bookingCode && category ? "col-span-2" : ""}>
                    <div className="text-slate-500">Donor Pass ID</div>
                    <div className="font-mono text-[13px]">{ticketId}</div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={closeSheetAndResume}
                className="rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300"
              >
                Close & Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
